import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestProviderService } from '../rest-provider/rest-provider.service';

/**
 * Review state of one uploaded file, as served by a module's "review state" endpoint and
 * rendered by sem-file-upload (badge + confirm/reject buttons). Status: 0 pending, 1 confirmed,
 * 2 rejected. The state is deliberately NOT part of the form model, so a form save can never
 * overwrite a review.
 */
export interface FileReviewState {
  FileID: number;
  FileName?: string;
  FileSectionType?: number;
  Status: number;
  StatusTitle?: string;
  ReviewedBy?: string;
  ReviewedDate?: string;
  Comment?: string;
}

export interface FileReviewSectionState {
  FileSectionType: number;
  Title?: string;
  Required?: boolean;
  FileCount?: number;
  /** Missing | Pending | Rejected | Confirmed */
  State: string;
}

export interface FileReviewStateResponse {
  DocumentId?: number;
  StatusId?: number;
  StatusTitle?: string;
  Complete?: boolean;
  Files: FileReviewState[];
  Sections: FileReviewSectionState[];
}

/**
 * One request per (url, documentId) shared by every sem-file-upload instance on the form
 * (a form with N file sections would otherwise fetch the same state N times). Instances
 * subscribe to the subject and call refresh() after a review action.
 */
@Injectable({ providedIn: 'root' })
export class FileReviewStateService {

  private subjects: Map<string, BehaviorSubject<FileReviewStateResponse>> = new Map();
  private inFlight: Set<string> = new Set();

  constructor(private restService: RestProviderService) { }

  state(url: string, documentId: number): Observable<FileReviewStateResponse> {
    const key = this.key(url, documentId);

    if (!this.subjects.has(key)) {
      this.subjects.set(key, new BehaviorSubject<FileReviewStateResponse>(null));
      this.load(url, documentId);
    }

    return this.subjects.get(key).asObservable();
  }

  refresh(url: string, documentId: number) {
    const key = this.key(url, documentId);

    if (!this.subjects.has(key)) {
      this.subjects.set(key, new BehaviorSubject<FileReviewStateResponse>(null));
    }

    this.load(url, documentId);
  }

  private load(url: string, documentId: number) {
    const key = this.key(url, documentId);

    if (this.inFlight.has(key)) {
      return;
    }

    this.inFlight.add(key);

    const separator = url.indexOf('?') >= 0 ? '&' : '?';

    this.restService.getDataGET(url + separator + 'documentId=' + documentId, 'application/json').subscribe(data => {
      this.inFlight.delete(key);

      let parsed: FileReviewStateResponse = null;
      try {
        parsed = typeof data === 'string' ? JSON.parse(data) : data as any;
      } catch (e) {
        parsed = null;
      }

      if (parsed && !Array.isArray(parsed.Files)) {
        parsed.Files = [];
      }
      if (parsed && !Array.isArray(parsed.Sections)) {
        parsed.Sections = [];
      }

      this.subjects.get(key).next(parsed);
    }, error => {
      this.inFlight.delete(key);
      console.log('File review state request failed', error);
    });
  }

  private key(url: string, documentId: number): string {
    return (url || '') + '#' + documentId;
  }
}
